import config from "config";
import { gql, useLazyQuery, useQuery } from "@apollo/client";
import {
  ENTITY_TYPE,
  IAggregate,
  ICenterEntity,
  ICountryEntity,
  IEntity,
} from "types";
import { useCheckIsAdmin, useCheckPremium, useCheckSuper } from "utils";
import { CENTER_FIELDS } from "./Center.constants";
import { useRef, useState } from "react";

const GET_CENTERS = gql`
  query getCenters(
    $where: EntityWhere
    $sort: [EntitySort!]
    $limit: Int
    $offset: Int
    $phrase: String!
    $isAdmin: Boolean!
    $isAuth: Boolean!
  ) {
    centersAggregate: entitiesAggregate(where: $where, fulltext: { FullTextSearchFields: { phrase: $phrase } }) {
			count
		}
    centers: entities(
      options: {
        limit: $limit,
        offset: $offset,
        sort: $sort
      },
      where: $where
      fulltext: { FullTextSearchFields: { phrase: $phrase } }
      ) {
      ${CENTER_FIELDS}
    }
    countries @include(if: $isAuth) {
      name
    }
    cityList @include(if: $isAuth)
  }
`;

type CenterVariableType = {
  where?: { [x: string]: any };
  sort?: {};
  limit?: number;
  offset?: number;
  search?: string | null;
  phrase?: string;
  isAdmin?: boolean;
};

export function useGraphQLCenters({
  limit,
  offset,
  sort,
  where,
  search,
}: CenterVariableType) {
  const isAdmin = useCheckIsAdmin();
  const isPremium = useCheckPremium();
  const { loading, error, data } = useQuery<{
    centers: Array<ICenterEntity>;
    centersAggregate: IAggregate<{}>;
    countries: Partial<ICountryEntity>[];
    cityList: string[];
  }>(GET_CENTERS, {
    variables: {
      limit: limit,
      where: {
        ...where,
        verticalProfiles_SOME: {
          ...where?.verticalProfiles_SOME,
          vertical: config.VERTICAL,
          entityTypeProfiles_SOME: {
            ...where?.verticalProfiles_SOME?.entityTypeProfiles_SOME,
            entityType: ENTITY_TYPE.CENTER,
          },
        },
      },
      sort: sort,
      offset: offset,
      phrase: search
        ? "+" + search.replaceAll("-", " ").replaceAll(" ", " +") + "*"
        : "*",
      isAdmin: isAdmin,
      isAuth: isPremium,
    },
  });

  return { loading, error, data };
}

export function useCentersExportQuery({
  sort,
  where,
  search,
}: CenterVariableType) {
  const isAdmin = useCheckIsAdmin();
  const isSuper = useCheckSuper();
  const isPremium = useCheckPremium();

  const exportDataRef = useRef<IEntity[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [getCount] = useLazyQuery(GET_CENTERS_COUNT);

  const [getCenters] = useLazyQuery<{ centers: IEntity[] }>(GET_CENTERS_EXPORT);

  const getExportData = async () => {
    setIsFetching(true);

    try {
      const { data: countResult } = await getCount({
        variables: {
          where: {
            ...where,
            verticalProfiles_SOME: {
              ...where?.verticalProfiles_SOME,
              vertical: config.VERTICAL,
              entityTypeProfiles_SOME: {
                ...where?.verticalProfiles_SOME?.entityTypeProfiles_SOME,
                entityType: ENTITY_TYPE.CENTER,
              },
            },
          },
          phrase: search
            ? "+" + search.replaceAll("-", " ").replaceAll(" ", " +") + "*"
            : "*",
          isAdmin: isAdmin,
          isAuth: isPremium,
        },
      });

      const isPrivilegedUser = isAdmin || isSuper;

      const totalCenters = isPrivilegedUser
        ? countResult?.centersAggregate?.count
        : Math.min(200, countResult?.centersAggregate?.count || 0);

      if (totalCenters === 0) {
        setIsFetching(false);
        return;
      }

      const LIMIT = !isPrivilegedUser ? totalCenters : 500;
      const totalRequests = Math.ceil(totalCenters / LIMIT);

      let accumulatedData: IEntity[] = [];

      for (let i = 0; i < totalRequests; i++) {
        const { data } = await getCenters({
          variables: {
            limit: LIMIT,
            offset: i * LIMIT,
            where: {
              ...where,
              verticalProfiles_SOME: {
                ...where?.verticalProfiles_SOME,
                vertical: config.VERTICAL,
                entityTypeProfiles_SOME: {
                  ...where?.verticalProfiles_SOME?.entityTypeProfiles_SOME,
                  entityType: ENTITY_TYPE.CENTER,
                },
              },
            },
            sort: sort,
            phrase: search
              ? "+" + search.replaceAll("-", " ").replaceAll(" ", " +") + "*"
              : "*",
            isAdmin: isAdmin,
            isAuth: isPremium,
          },
        });

        if (data?.centers) {
          accumulatedData = [...accumulatedData, ...data.centers];
        }
      }

      exportDataRef.current = accumulatedData;
    } catch (err) {
      console.error(err);
      setError(err as Error);
    } finally {
      setIsFetching(false);
    }
  };

  return {
    loadingExport: isFetching,
    dataExport: exportDataRef.current,
    error,
    getExportData,
  };
}

const GET_CENTERS_COUNT = gql`
  query getCentersCount($where: EntityWhere, $phrase: String!) {
    centersAggregate: entitiesAggregate(
      where: $where
      fulltext: { FullTextSearchFields: { phrase: $phrase } }
    ) {
      count
    }
  }
`;

const GET_CENTERS_EXPORT = gql`
  query getCentersExport(
    $where: EntityWhere
    $sort: [EntitySort!]
    $limit: Int
    $offset: Int
    $phrase: String!
    $isAdmin: Boolean!
    $isAuth: Boolean!
  ) {
    centers: entities (
      options: {
        limit: $limit,
        offset: $offset,
        sort: $sort
      },
      where: $where
      fulltext: { FullTextSearchFields: { phrase: $phrase } }
      ) {
      ${CENTER_FIELDS}
    }
  }
`;
